import { Icon as BlueIcon } from '@blueprintjs/core';

import { useState as useHookState } from '@hookstate/core';

import ProfileDefaultPNG from '@assets/png/profile-default.png';
import ImagePlaceholderPNG from '@assets/png/document-placeholder.png';
import ProductPlaceholderPNG from '@assets/png/product.png';

import { format } from 'date-fns';
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  createTaskComment,
  deleteTaskFile,
  getTask,
  patchTask,
  removeTaskProducts,
} from '@services/queries/tasks';
import { useToast } from '@hooks/index';
import { DeleteDialog } from '@components/index';

import { ITaskHistory, ITaskProduct } from '@models/Task.model';
import {
  Close,
  Header,
  Divider,
  HeaderLeft,
  HeaderRight,
  SubjectContainer,
  Iconleft,
  Attachments,
  SectionTitle,
  SectionTitleText,
  SectionTitleButton,
  FilesList,
  FileContainer,
  FileImageContainer,
  FileImageAvatar,
  FileImageClose,
  FileImageName,
  FileImageDate,
  FileImage,
  Relationships,
  EmptySection,
  SubjectInput,
  DescriptionInput,
  Sides,
  History,
  HistoryList,
  HistoryItem,
  HistoryUser,
  HistoryUserAvatar,
  HistoryUserName,
  HistoryHeader,
  HistoryItemTitle,
  HistoryItemDate,
  HistoryChange,
  Products,
  ProductContainer,
  ProductHeader,
  ProductTitle,
  ProductGrid,
  ProductText,
  ProductCell,
  FullDetails,
  CloseProduct,
  ProductImage,
  LeftSide,
  SeeMore,
  SeeMoreText,
  Empty,
} from './DetailsDialog.styles';

import { TaskPriorityFlag } from '../TaskPriorityFlag/TaskPriorityFlag';
import { TaskStatusLabel } from '../TaskStatusLabel/TaskStatusLabel';
import { TaskDate } from '../TaskDate/TaskDate';

// import { TaskCategoryLabel } from '../TaskCategoryLabel/TaskCategoryLabel';
import { TaskAssigneeLabel } from '../TaskAssigneeLabel/TaskAssigneeLabel';
import { AddFileDialog } from '../AddFileDialog/AddFileDialog';

import { DetailsDialogLoading } from './DetailsDialog.loading';
import { HistoryTextFormat } from '../HistoryTextFormat/HistoryTextFormat';
import { HistoryContent } from './components/HistoryContent/HistoryContent';
import { AddProducts } from './components/AddProducts/AddProducts';
import { ProductDetails } from './components/ProductDetails/ProductDetails';
import { ProductsList } from './components/ProductsList/ProductsList';

interface IDetailsDialog {
  onClose: () => void;
  taskId: string;
}

export const DetailsDialogRaw = ({ onClose, taskId }: IDetailsDialog) => {
  const [openAddFile, setOpenAddFile] = useState(false);
  const [openAddProduct, setOpenAddProduct] = useState(false);
  const [openProductsList, setOpenProductsList] = useState(false);
  const [openProductDetails, setOpenProductDetails] = useState(false);
  const [selectedFileId, setSelectedFileId] = useState<string | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<ITaskProduct | null>(
    null,
  );
  const [newTitle, setNewTitle] = useState<string>('');
  const [newDescription, setNewDescription] = useState<string>('');
  const [newComment, setNewComment] = useState<string>('');

  const openDeleteFile = useHookState(false);
  const openRemoveProduct = useHookState(false);

  const titleRef = useRef<HTMLInputElement>(null);
  const descriptionRef = useRef<HTMLTextAreaElement>(null);

  const { toast } = useToast();
  const queryClient = useQueryClient();

  const createTaskCommentMutation = useMutation(createTaskComment, {
    onSuccess: async () => {
      queryClient.invalidateQueries(['tasks']);

      toast.success('Comment Added successfully');
    },
    onError: () => {
      toast.error('Something got wrong');
    },
  });

  const mutationDeleteFile = useMutation(deleteTaskFile, {
    onSuccess: async () => {
      queryClient.invalidateQueries(['tasks']);

      toast.success('File Deleted successfully');
    },
    onError: () => {
      toast.error('Something got wrong');
    },
  });

  const mutationRemoveProduct = useMutation(removeTaskProducts, {
    onSuccess: async () => {
      queryClient.invalidateQueries(['tasks']);

      toast.success('Product Removed successfully');
    },
    onError: () => {
      toast.error('Something got wrong');
    },
  });

  const patchTaskMutation = useMutation(patchTask, {
    onSuccess: async () => {
      queryClient.invalidateQueries(['tasks']);
    },
  });

  const { isLoading, data: task } = useQuery(['tasks', taskId], async () => {
    return getTask({
      taskId,
    });
  });

  useEffect(() => {
    if (task) {
      setNewTitle(task.title);
      setNewDescription(task.description);
    }
  }, [task]);

  const parseHistoryTitle = (item: ITaskHistory) => {
    const typeFirstLetter = item.type.slice(0, 1).toUpperCase();
    const typeRestOfString = item.type.slice(1);
    const type = `${typeFirstLetter}${typeRestOfString}`;

    if (item.action === 'added') {
      if (item.type === 'relationship') return `Added new Relationships`;
      if (item.type === 'date') return `Added a Due Date`;
      if (item.type === 'file') return `Added a new Attachment`;
      if (item.type === 'assignee') return `Added an ${type}`;

      return `Added a new ${type}`;
    }
    if (item.action === 'changed') {
      if (item.type === 'date') return `Changed the Due Date`;

      return `Changed the ${type}`;
    }
    if (item.action === 'removed') {
      if (item.type === 'file') return `Deleted an Attachment`;
      if (item.type === 'assignee') return `Removed the ${type}`;

      return `Removed a ${type}`;
    }

    return `${item.action} ${type}`;
  };

  const myRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    if (myRef.current) {
      myRef.current.scrollTop = myRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [task, myRef.current?.scrollHeight]);

  if (isLoading || !task) {
    return <DetailsDialogLoading />;
  }

  return (
    <>
      <Header>
        <HeaderLeft>
          <TaskDate task={task} />
          <TaskStatusLabel task={task} />
        </HeaderLeft>

        <HeaderRight>
          {/* <TaskCategoryLabel task={task} /> */}
          <TaskPriorityFlag task={task} withText />
          <TaskAssigneeLabel task={task} />

          <Close onClick={onClose}>
            <BlueIcon icon="cross" color="#8F95B2" />
          </Close>
        </HeaderRight>
      </Header>
      <Divider />
      <Sides>
        <LeftSide>
          <SubjectContainer>
            {task.priority && ['important', 'urgent'].includes(task.priority) && (
              <Iconleft>
                <BlueIcon icon="double-chevron-up" color="#FF9900" />
              </Iconleft>
            )}
            <SubjectInput
              ref={titleRef}
              value={newTitle}
              onChange={e => setNewTitle(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  titleRef.current?.blur();
                }
              }}
              onBlur={() => {
                if (newTitle.trim()) {
                  if (newTitle.trim() !== task.title) {
                    patchTaskMutation.mutate({
                      taskId,
                      title: newTitle,
                    });
                  }
                } else {
                  setNewTitle(task.title);
                }
              }}
            />
          </SubjectContainer>

          <DescriptionInput
            ref={descriptionRef}
            value={newDescription}
            onChange={e => setNewDescription(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                descriptionRef.current?.blur();
              }
            }}
            onBlur={() => {
              if (newDescription.trim()) {
                if (newDescription.trim() !== task.description) {
                  patchTaskMutation.mutate({
                    taskId,
                    description: newDescription,
                  });
                }
              } else {
                setNewDescription(task.description);
              }
            }}
          />

          <Relationships>
            <SectionTitle>
              <SectionTitleText>Relationships</SectionTitleText>
              <SectionTitleButton onClick={() => setOpenAddProduct(true)}>
                + Add
              </SectionTitleButton>
            </SectionTitle>
            {task.products.length > 0 ? (
              <>
                <ProductHeader>
                  <ProductTitle>Name</ProductTitle>
                  <ProductTitle>Model #</ProductTitle>
                  <ProductTitle>Manufacturer</ProductTitle>
                </ProductHeader>
                <Products>
                  {task.products.slice(0, 3).map(item => (
                    <ProductContainer key={item.id}>
                      <ProductGrid>
                        <ProductCell>
                          <ProductImage
                            src={item.imageUrl || ProductPlaceholderPNG}
                            brokenSrc={ProductPlaceholderPNG}
                          />
                          {item.name ? (
                            <ProductText>{item.name}</ProductText>
                          ) : (
                            <Empty>Empty</Empty>
                          )}
                        </ProductCell>
                        <ProductCell>
                          {item.model ? (
                            <ProductText>{item.model}</ProductText>
                          ) : (
                            <Empty>Empty</Empty>
                          )}
                        </ProductCell>
                        <ProductCell>
                          {item.manufacturerName ? (
                            <ProductText>{item.manufacturerName}</ProductText>
                          ) : (
                            <Empty>Empty</Empty>
                          )}
                        </ProductCell>
                        <ProductCell
                          onClick={() => {
                            setOpenProductDetails(true);
                            setSelectedProduct(item);
                          }}
                        >
                          <FullDetails>Full Details</FullDetails>
                        </ProductCell>
                        <ProductCell>
                          <CloseProduct
                            onClick={() => {
                              openRemoveProduct.set(true);
                              setSelectedProduct(item);
                            }}
                          >
                            <BlueIcon icon="cross" color="#8F95B2" />
                          </CloseProduct>
                        </ProductCell>
                      </ProductGrid>
                    </ProductContainer>
                  ))}
                </Products>
              </>
            ) : (
              <EmptySection>
                No relationships added. Click to add a new one.
              </EmptySection>
            )}
            {task.products.length > 2 && (
              <SeeMore
                onClick={e => {
                  e.stopPropagation();
                  setOpenProductsList(true);
                }}
              >
                <SeeMoreText>See more</SeeMoreText>
                <BlueIcon icon="chevron-down" color="#1371FE" size={10} />
              </SeeMore>
            )}
          </Relationships>

          <Attachments>
            <SectionTitle>
              <SectionTitleText>Attachments</SectionTitleText>
              <SectionTitleButton onClick={() => setOpenAddFile(true)}>
                + Add
              </SectionTitleButton>
            </SectionTitle>
            {task.files.length > 0 ? (
              <FilesList>
                {task.files.map(item => (
                  <FileContainer>
                    <FileImageContainer>
                      <FileImage
                        src={item.filename}
                        brokenSrc={ImagePlaceholderPNG}
                      />
                      <FileImageClose
                        onClick={() => {
                          openDeleteFile.set(true);
                          setSelectedFileId(item.id);
                        }}
                      >
                        <BlueIcon icon="cross" color="#8F95B2" />
                      </FileImageClose>
                      <FileImageAvatar
                        src={item.userAvatar}
                        brokenSrc={ProfileDefaultPNG}
                      />
                    </FileImageContainer>
                    <FileImageName>{item.originalname}</FileImageName>
                    <FileImageDate>
                      {format(item.createdAt, 'MMM d - hh:mma')}
                    </FileImageDate>
                  </FileContainer>
                ))}
              </FilesList>
            ) : (
              <EmptySection>
                No attachments added. Click to add a new one.
              </EmptySection>
            )}
          </Attachments>
          {openAddFile && (
            <AddFileDialog
              task={task}
              open={openAddFile}
              onClose={() => setOpenAddFile(false)}
            />
          )}
          {openDeleteFile.value && selectedFileId && (
            <DeleteDialog
              open={openDeleteFile}
              onDelete={() => {
                mutationDeleteFile.mutate({
                  taskId,
                  taskFileId: selectedFileId,
                });
                openDeleteFile.set(false);
              }}
            />
          )}
          {openRemoveProduct.value && selectedProduct && (
            <DeleteDialog
              open={openRemoveProduct}
              onDelete={() => {
                mutationRemoveProduct.mutate({
                  taskId,
                  productsIds: [selectedProduct.id],
                });
                openRemoveProduct.set(false);
              }}
              buttonText="Remove"
            />
          )}
          {openAddProduct && (
            <AddProducts
              open={openAddProduct}
              onClose={() => setOpenAddProduct(false)}
              taskId={taskId}
              productsIdsAdded={task.products.map(item => item.id)}
            />
          )}
          {openProductDetails && selectedProduct && (
            <ProductDetails
              open={openProductDetails}
              onClose={() => setOpenProductDetails(false)}
              product={selectedProduct}
            />
          )}
          {openProductsList && (
            <ProductsList
              open={openProductsList}
              onClose={() => setOpenProductsList(false)}
              ContentList={() => {
                return (
                  <>
                    <ProductHeader>
                      <ProductTitle>Name</ProductTitle>
                      <ProductTitle>Model #</ProductTitle>
                      <ProductTitle>Manufacturer</ProductTitle>
                    </ProductHeader>
                    <Products>
                      {task.products.map(item => (
                        <ProductContainer key={item.id}>
                          <ProductGrid>
                            <ProductCell>
                              <ProductImage
                                src={item.imageUrl || ProductPlaceholderPNG}
                                brokenSrc={ProductPlaceholderPNG}
                              />
                              {item.name ? (
                                <ProductText>{item.name}</ProductText>
                              ) : (
                                <Empty>Empty</Empty>
                              )}
                            </ProductCell>
                            <ProductCell>
                              {item.model ? (
                                <ProductText>{item.model}</ProductText>
                              ) : (
                                <Empty>Empty</Empty>
                              )}
                            </ProductCell>
                            <ProductCell>
                              {item.manufacturerName ? (
                                <ProductText>
                                  {item.manufacturerName}
                                </ProductText>
                              ) : (
                                <Empty>Empty</Empty>
                              )}
                            </ProductCell>
                            <ProductCell
                              onClick={() => {
                                setOpenProductDetails(true);
                                setSelectedProduct(item);
                              }}
                            >
                              <FullDetails>Full Details</FullDetails>
                            </ProductCell>
                            <ProductCell>
                              <CloseProduct
                                onClick={() => {
                                  openRemoveProduct.set(true);
                                  setSelectedProduct(item);
                                }}
                              >
                                <BlueIcon icon="cross" color="#8F95B2" />
                              </CloseProduct>
                            </ProductCell>
                          </ProductGrid>
                        </ProductContainer>
                      ))}
                    </Products>
                  </>
                );
              }}
            />
          )}
        </LeftSide>
        <History>
          <SectionTitleText>History</SectionTitleText>
          <HistoryList ref={myRef}>
            {task.history.length === 0 ? (
              <EmptySection>
                No changes yet. Task changes and comments will show here.
              </EmptySection>
            ) : (
              <>
                {task.history.map(item => (
                  <HistoryItem key={item.id}>
                    <HistoryHeader>
                      <HistoryUser>
                        <HistoryUserAvatar
                          src={item.userAvatar || ProfileDefaultPNG}
                          brokenSrc={ProfileDefaultPNG}
                        />
                        <HistoryUserName>{item.userName}</HistoryUserName>
                      </HistoryUser>
                      <HistoryItemTitle>
                        {parseHistoryTitle(item)}
                      </HistoryItemTitle>
                      <HistoryItemDate>
                        {format(item.createdAt, 'MMM d - hh:mma')}
                      </HistoryItemDate>
                    </HistoryHeader>
                    <HistoryChange>
                      <HistoryContent history={item} />
                    </HistoryChange>
                  </HistoryItem>
                ))}
              </>
            )}
          </HistoryList>
          <HistoryTextFormat
            onMutateMessage={() => {
              createTaskCommentMutation.mutate({
                taskId,
                comment: newComment,
              });
            }}
            text={newComment}
            setText={setNewComment}
          />
        </History>
      </Sides>
    </>
  );
};
